export const workData = [
  {
    id: "eco-tracker",
    title: "Eco-Feedback for LLMs",
    brief:
      "A system that monitors LLM token usage and estimates energy consumption in real time. I implemented token tracking, runtime data processing, and a state-sync mechanism to improve long conversation efficiency.",
  
    skills: ["React", "JavaScript", "System Design", "Data Visualization", "Performance Optimization"],
  
    steps: [
      {
        title: "Project Overview",
        desc: "Large Language Model (LLM) interactions consume computational resources that are typically invisible to users. This project explores how software systems can monitor these hidden costs by capturing token usage during runtime and translating them into estimated energy consumption metrics. The goal was to build a system that makes model usage observable through real-time tracking and data processing.",
        highlight: "System goal — capturing and exposing hidden model usage metrics at runtime.",
        img: "/assets/eco-tracker/eco-feedback-design.png"
      },
  
      {
        title: "Inspiration & Pattern Research",
        desc: "To design an effective monitoring interface, I analyzed several existing resource-tracking systems such as energy dashboards and activity monitoring tools. From this analysis, I extracted common interaction patterns for real-time feedback and resource visualization, which informed the structure of the monitoring interface and the way usage data is communicated to users.",
        highlight: "Design strategy — adapting proven monitoring patterns for system resource visibility.",
        img: "/assets/eco-tracker/features-map.png"
      },
  
      {
        title: "Concept Ideation",
        desc: "Multiple conceptual directions were explored to determine how system metrics could be communicated clearly to users. Different visualization metaphors were tested to connect token consumption with environmental impact. The final direction focused on a growth-based ecological metaphor to represent accumulated usage over time.",
        highlight: "Concept exploration — testing different ways to represent system metrics through visual metaphors.",
        img: [
          "/assets/pocket-psychologist/eight-idea.jpeg",
          "/assets/pocket-psychologist/simplehtml.png"
        ]
      },
  
      {
        title: "Prototyping & Feedback",
        desc: "Initial versions of the interface were implemented using static HTML and CSS layouts to evaluate clarity and usability. Early testing revealed that raw numerical token values were difficult to interpret, and that multiple resource indicators increased cognitive load. Based on this feedback, the monitoring model was simplified into a single resource metric with clearer visual signals.",
        highlight: "Iteration — simplifying system feedback to improve interpretability of usage data.",
        img: "/assets/pocket-psychologist/mid-fi-deisgn.png"
      },
  
      {
        title: "Frontend Implementation | Monitoring",
        desc: "I implemented the monitoring logic within the React application by capturing token usage metadata returned from the OpenAI API after each request. These values are processed using an energy estimation formula and stored in application state. Chart.js is used to render dynamic charts that display token consumption and estimated energy cost trends in real time.",
        highlight: "Implementation — real-time token monitoring and dynamic usage visualization.",
        img: "/assets/eco-tracker/eco-deisgn.png"
      },
  
      {
        title: "Frontend Optimization | State-Sync",
        desc: "To address inefficiencies in long conversations, I implemented a State-Sync mechanism that extracts key information from conversation history and stores it as structured JSON summaries. These summaries are injected into subsequent prompts to maintain context while reducing redundant token usage and preventing exponential growth in prompt size.",
        highlight: "Optimization — structured state synchronization for efficient long-context interactions.",
        img: "/assets/eco-tracker/remberFunction.png"
      }
  
    ]
  },

  {
    id: "pocket-psychologist",
    title: "Pocket Psychologist",
    brief:
      "A UX app concept for OCD support, calm interaction design and behavioral psychology for immediate and long-term self-management. I led research, ideation, and end-to-end prototyping.",
    skills: ["Figma", "Behavioral Design", "User Research", "Usability Testing", "High-Fidelity Prototyping"],
    steps: [
      {
        title: "Concept & Vision",
        desc: "Pocket Psychologist is a digital mental health app concept designed for users with Obsessive-Compulsive Disorder (OCD). This project explores how mobile interactions and behavioral science can support users in achieving immediate emotional relief during high-anxiety situations, while gradually building long-term self-management capabilities in daily life.",
        highlight: "Design highlight — Focused on creating immediate emotional relief while supporting long-term behavioral change.",
        img: "/assets/pocket-psychologist/concept.png"
      },
      {
        title: "User Research Insights",
        desc: "In the initial phase, I conducted literature research, analyzed user narratives from online communities and forums, and performed competitive analysis of existing OCD apps to understand the real challenges OCD users face in emotional, social, and privacy dimensions. I then translated these insights into clear user needs.",
        highlight: "Research highlight — Combined academic research and real user stories to identify key pain points in OCD management.",
        img: "/assets/pocket-psychologist/research.png"
      },
      {
        title: "Ideation & Sketching",
        desc: "Based on core user needs, I used Crazy 8s to rapidly explore multiple design directions, combining low-fidelity sketches and storyboards to transform abstract emotional problems into concrete use cases and interaction flows. The focus of this phase was on structuring the overall logic and reducing users' psychological and cognitive burden, rather than visual details.",
        highlight: "Ideation highlight — Rapidly generated multiple solutions while prioritizing cognitive load reduction.",
        img: "/assets/pocket-psychologist/ideation-storyboard.png"
      },
      {
        title: "Prototype & Testing",
        desc: "The final solution was integrated into a high-fidelity interactive prototype. The visual style was deliberately kept simple and restrained to avoid causing additional stimulation to users in anxious states. Feedback was collected through usability testing, and iterative optimizations were made for key issues such as emergency feature visibility and operational burden.",
        highlight: "Testing highlight — Iteratively refined prototype to ensure usability and emotional safety under high-stress conditions.",
        img: "/assets/pocket-psychologist/final-prototype.png"
      }
    ]
  }
];